import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pmsKeys } from '../queryKeys';
import { bandsApi } from './bands.api';
import type {
  CreateBandPayload,
  UpdateBandPayload,
  ReplaceBandsPayload,
  PerformanceBand,
  ValidateBandsPayload,
} from '../types';

export function usePmsBands() {
  return useQuery({
    queryKey: pmsKeys.bands(),
    queryFn: () => bandsApi.list(),
    staleTime: 5 * 60 * 1000,
    select: (d) =>
      (
        (Array.isArray(d) ? d : ((d as { data?: unknown })?.data ?? [])) as PerformanceBand[]
      ).sort((a, b) => a.sort_order - b.sort_order),
  });
}

export function useCreatePmsBand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBandPayload) => bandsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.bands() }),
  });
}

/**
 * Save the whole band ladder in one atomic, validated request.
 *
 * The editor's save path. It replaced a `Promise.all` of one request per changed
 * row: the server saw invalid intermediate states in a non-deterministic order
 * (so tiling could not be enforced at all), and a partial failure left the ladder
 * half-written with a gap in it.
 */
export function useReplacePmsBands() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ReplaceBandsPayload) => bandsApi.replaceAll(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.bands() }),
  });
}

export function useUpdatePmsBand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bandId, data }: { bandId: string; data: UpdateBandPayload }) =>
      bandsApi.update(bandId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.bands() }),
  });
}

export function useDeletePmsBand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bandId: string) => bandsApi.delete(bandId),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.bands() }),
  });
}

export function useValidatePmsBands() {
  return useMutation({
    mutationFn: (data: ValidateBandsPayload) => bandsApi.validate(data),
  });
}
